import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useScreenInit } from '../useScreenInit.js';
import { ArrowLeft, ShoppingBag, CreditCard, Truck, ShieldCheck, ChevronRight } from 'lucide-react';
import { useStore } from '../store/useStore';
export function Checkout() {
  useScreenInit();
  const {
    cart,
    toggleCart
  } = useStore();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const subtotal = cart.reduce((acc, item) => {
    const price = parseFloat((item.salePrice || item.price).replace('$', ''));
    return acc + price * item.quantity;
  }, 0);
  const shipping = 15.0;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;
  if (cart.length === 0) {
    return <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 text-center" data-id="element-425">
        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center text-gray-300 mb-6" data-id="element-426">
          <ShoppingBag size={40} data-id="element-427" />
        </div>
        <h2 className="text-3xl font-serif mb-4" data-id="element-428">Your bag is empty</h2>
        <p className="text-gray-500 mb-8 max-w-md" data-id="element-429">
          You need to add some items to your bag before you can checkout.
        </p>
        <Link to="/shop" className="bg-black text-white px-10 py-4 text-xs uppercase tracking-widest font-bold hover:bg-gold transition-all duration-300" data-id="element-430">
          Return to Shop
        </Link>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 pt-32 pb-20" data-id="element-431">
      <div className="container mx-auto px-4" data-id="element-432">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-6" data-id="element-433">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold hover:text-gold transition-colors" data-id="element-434">
            <ArrowLeft size={16} data-id="element-435" /> Back
          </button>
          <h1 className="text-3xl md:text-5xl font-serif" data-id="element-436">Checkout</h1>
          <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest font-bold text-gray-400" data-id="element-437">
            <span className={step >= 1 ? 'text-black' : ''} data-id="element-438">Shipping</span>
            <ChevronRight size={12} data-id="element-439" />
            <span className={step >= 2 ? 'text-black' : ''} data-id="element-440">Payment</span>
            <ChevronRight size={12} data-id="element-441" />
            <span className={step >= 3 ? 'text-black' : ''} data-id="element-442">Review</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-443">
          {/* Main Form Area */}
          <div className="lg:col-span-7 space-y-8" data-id="element-444">
            {/* Shipping Section */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white p-8 shadow-sm border border-gray-100" data-id="element-445">
              <div className="flex items-center gap-4 mb-8" data-id="element-446">
                <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center font-bold" data-id="element-447">
                  1
                </div>
                <h2 className="text-xl font-serif" data-id="element-448">Shipping Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-449">
                <div className="space-y-2" data-id="element-450">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-451">
                    First Name
                  </label>
                  <input type="text" className="w-full border-b border-gray-200 py-3 focus:border-gold outline-none transition-colors text-sm" placeholder="Enter your first name" data-id="element-452" />
                </div>
                <div className="space-y-2" data-id="element-453">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-454">
                    Last Name
                  </label>
                  <input type="text" className="w-full border-b border-gray-200 py-3 focus:border-gold outline-none transition-colors text-sm" placeholder="Enter your last name" data-id="element-455" />
                </div>
                <div className="md:col-span-2 space-y-2" data-id="element-456">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-457">
                    Email Address
                  </label>
                  <input type="email" className="w-full border-b border-gray-200 py-3 focus:border-gold outline-none transition-colors text-sm" placeholder="Enter your email address" data-id="element-458" />
                </div>
                <div className="md:col-span-2 space-y-2" data-id="element-459">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-460">
                    Street Address
                  </label>
                  <input type="text" className="w-full border-b border-gray-200 py-3 focus:border-gold outline-none transition-colors text-sm" placeholder="Enter your street address" data-id="element-461" />
                </div>
                <div className="space-y-2" data-id="element-462">
                  <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-463">
                    City
                  </label>
                  <input type="text" className="w-full border-b border-gray-200 py-3 focus:border-gold outline-none transition-colors text-sm" placeholder="Enter your city" data-id="element-464" />
                </div>
                <div className="grid grid-cols-2 gap-6" data-id="element-465">
                  <div className="space-y-2" data-id="element-466">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-467">
                      State
                    </label>
                    <input type="text" className="w-full border-b border-gray-200 py-3 focus:border-gold outline-none transition-colors text-sm" placeholder="State" data-id="element-468" />
                  </div>
                  <div className="space-y-2" data-id="element-469">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-470">
                      ZIP Code
                    </label>
                    <input type="text" className="w-full border-b border-gray-200 py-3 focus:border-gold outline-none transition-colors text-sm" placeholder="ZIP" data-id="element-471" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Payment Section */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white p-8 shadow-sm border border-gray-100" data-id="element-472">
              <div className="flex items-center gap-4 mb-8" data-id="element-473">
                <div className="w-10 h-10 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center font-bold" data-id="element-474">
                  2
                </div>
                <h2 className="text-xl font-serif" data-id="element-475">Payment Method</h2>
              </div>

              <div className="space-y-6" data-id="element-476">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-477">
                  <button className="border-2 border-black p-4 flex items-center justify-between group" data-id="element-478">
                    <div className="flex items-center gap-3" data-id="element-479">
                      <CreditCard size={20} data-id="element-480" />
                      <span className="text-xs font-bold uppercase tracking-widest" data-id="element-481">
                        Credit Card
                      </span>
                    </div>
                    <div className="w-4 h-4 rounded-full border-4 border-black" data-id="element-482" />
                  </button>
                  <button className="border-2 border-gray-100 p-4 flex items-center justify-between group hover:border-gray-200 transition-colors" data-id="element-483">
                    <div className="flex items-center gap-3" data-id="element-484">
                      <span className="text-xs font-bold uppercase tracking-widest text-gray-400" data-id="element-485">
                        PayPal
                      </span>
                    </div>
                    <div className="w-4 h-4 rounded-full border-2 border-gray-200" data-id="element-486" />
                  </button>
                </div>

                <div className="space-y-6 pt-4" data-id="element-487">
                  <div className="space-y-2" data-id="element-488">
                    <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-489">
                      Card Number
                    </label>
                    <input type="text" className="w-full border-b border-gray-200 py-3 focus:border-gold outline-none transition-colors text-sm" placeholder="0000 0000 0000 0000" data-id="element-490" />
                  </div>
                  <div className="grid grid-cols-2 gap-6" data-id="element-491">
                    <div className="space-y-2" data-id="element-492">
                      <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-493">
                        Expiry Date
                      </label>
                      <input type="text" className="w-full border-b border-gray-200 py-3 focus:border-gold outline-none transition-colors text-sm" placeholder="MM / YY" data-id="element-494" />
                    </div>
                    <div className="space-y-2" data-id="element-495">
                      <label className="text-[10px] uppercase tracking-widest font-bold text-gray-500" data-id="element-496">
                        CVV
                      </label>
                      <input type="text" className="w-full border-b border-gray-200 py-3 focus:border-gold outline-none transition-colors text-sm" placeholder="123" data-id="element-497" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-5" data-id="element-498">
            <div className="sticky top-32 space-y-6" data-id="element-499">
              <div className="bg-white p-8 shadow-sm border border-gray-100" data-id="element-500">
                <h2 className="text-xl font-serif mb-8" data-id="element-501">Order Summary</h2>

                <div className="space-y-6 mb-8 max-h-[400px] overflow-y-auto pr-2" data-id="element-502">
                  {cart.map(item => <div key={item.id} className="flex gap-4" data-id="element-503">
                      <div className="w-16 h-20 bg-gray-50 flex-shrink-0 overflow-hidden" data-id="element-504">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-505" />
                      </div>
                      <div className="flex-1" data-id="element-506">
                        <h4 className="text-[10px] font-bold uppercase tracking-widest mb-1" data-id="element-507">
                          {item.name}
                        </h4>
                        <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-2" data-id="element-508">
                          Qty: {item.quantity}
                        </p>
                        <p className="text-xs font-serif text-gold" data-id="element-509">
                          {item.salePrice || item.price}
                        </p>
                      </div>
                    </div>)}
                </div>

                <div className="space-y-4 pt-6 border-t border-gray-100" data-id="element-510">
                  <div className="flex justify-between text-sm" data-id="element-511">
                    <span className="text-gray-500" data-id="element-512">Subtotal</span>
                    <span className="font-bold" data-id="element-513">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-514">
                    <span className="text-gray-500" data-id="element-515">Shipping</span>
                    <span className="font-bold" data-id="element-516">${shipping.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-517">
                    <span className="text-gray-500" data-id="element-518">Estimated Tax</span>
                    <span className="font-bold" data-id="element-519">${tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg pt-4 border-t border-gray-100" data-id="element-520">
                    <span className="font-serif" data-id="element-521">Total</span>
                    <span className="font-bold text-gold" data-id="element-522">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>

                <button className="w-full bg-black text-white py-5 mt-8 text-xs uppercase tracking-[0.2em] font-bold hover:bg-gold transition-all duration-500 shadow-xl shadow-gold/10" data-id="element-523">
                  Place Order
                </button>

                <div className="mt-8 space-y-4" data-id="element-524">
                  <div className="flex items-center gap-3 text-[10px] text-gray-400 uppercase tracking-widest" data-id="element-525">
                    <Truck size={16} className="text-gold" data-id="element-526" />
                    <span data-id="element-527">Free shipping on orders over $150</span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-gray-400 uppercase tracking-widest" data-id="element-528">
                    <ShieldCheck size={16} className="text-gold" data-id="element-529" />
                    <span data-id="element-530">Secure encrypted checkout</span>
                  </div>
                </div>
              </div>

              {/* Promo Code */}
              <div className="bg-white p-6 shadow-sm border border-gray-100" data-id="element-531">
                <div className="flex gap-4" data-id="element-532">
                  <input type="text" className="flex-1 border-b border-gray-200 py-2 focus:border-gold outline-none transition-colors text-xs uppercase tracking-widest" placeholder="Promo Code" data-id="element-533" />
                  <button className="text-[10px] uppercase tracking-widest font-bold hover:text-gold transition-colors" data-id="element-534">
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}